// ScreenBoundary — no single screen may ever black out the whole console.
//
// 2 Sept: PartyDetail destructured an unfinished query and the entire app
// unmounted to a void — twice in one night counting the unknown-URL hole. A
// desk tool fails LOUDLY AND SMALL: the broken screen says so and offers the
// way home; the rail, the palette and every other screen keep working.
import { Component, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/** THE BOUNDARY FORGETS WHEN THE SCREEN CHANGES (22 Sept 2026). A caught
 *  error is state, and this class sits above the routes — so the Exchanges
 *  crash of that morning followed the operator to Convert, to Trades, to
 *  every screen he opened next, and the console read as wholly broken when
 *  one screen was. Keyed on the path, React discards the failed boundary
 *  and mounts a fresh one on every navigation: the broken screen still says
 *  so, its neighbours are untouched. */
export function ScreenBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ScreenBoundaryFor key={pathname}>{children}</ScreenBoundaryFor>;
}

class ScreenBoundaryFor extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error('screen crashed:', error);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="card p-4 m-4 space-y-2">
          <p className="text-body font-medium text-tg-destructive">
            This screen hit an error and stopped.
          </p>
          <p className="text-meta text-tg-hint font-mono break-all">
            {this.state.error.message}
          </p>
          <p className="text-meta text-tg-hint">
            The books are untouched — a screen error can never post anything.
          </p>
          <a
            href="/"
            className="btn-secondary inline-block text-body py-1.5 px-3"
            onClick={() => this.setState({ error: null })}
          >
            ← Back
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}
